<template>
  <b-container fluid>
    <b-card
      v-if="config"
      no-body>
      <b-tabs
        card
        pills
        vertical>
        <b-tab
          v-for="(_, device) in config"
          :key="device"
          class="p-0 m-0"
          @click.prevent="setActiveDevice(device)">
          <template #title>
            <div
              class="p-0 m-0"
              @mousedown="showDeviceDeleteButton = true"
              @mouseover="showDeviceDeleteButton = device === activeDevice"
              @mouseleave="showDeviceDeleteButton = false">
              <span class="text-capitalize">
                {{ device }}
              </span>
              <span class="float-right m-0 p-0">
                <b-button
                  v-if="device === activeDevice && showDeviceDeleteButton"
                  variant="outline-danger"
                  size="sm"
                  style="margin-top:-3px;"
                  @click="toggleOverlay">
                  <b-icon icon="x" />
                </b-button>
              </span>
            </div>
          </template>
          <paywall-form
            ref="menuform"
            :placeholder="placeholder"
            :config="config"
            :device="device" />
        </b-tab>
        <template #tabs-end>
          <b-button
            class="mt-5"
            variant="success"
            @click="$bvModal.show(modalID)">
            <i class="fas fa-plus" />  Add Device
          </b-button>
        </template>
      </b-tabs>

      <b-modal
        :id="modalID"
        hide-footer
        title="Add New Device"
        @hidden="resetModal">
        <b-form-group>
          <b-input-group
            id="input-group-new-device"
            prepend="Name">
            <b-form-input
              id="input-new-device"
              v-model="newDeviceName"
              debounce="500"
              :state="validate()"
              placeholder="Enter device name" />
            <b-form-invalid-feedback class="text-capitalize">
              {{ errorMsg }}
            </b-form-invalid-feedback>
          </b-input-group>
        </b-form-group>
        <div
          class="mt-3 w-50">
          <b-button
            class="mr-3"
            variant="danger"
            @click="$bvModal.hide(modalID)">
            Cancel
          </b-button>
          <b-button
            :disabled="disableConfirm"
            variant="success"
            @click="addDevice">
            Confirm
          </b-button>
        </div>
      </b-modal>
    </b-card>

    <paywall-overlay
      :show="showOverlay"
      :msg="overlayMsg"
      @onConfirm="onConfirm"
      @onCancel="toggleOverlay" />
  </b-container>
</template>

<script>
import _ from "lodash"
import {mapGetters} from "vuex"
import PaywallForm from "@/components/PaywallV1/PaywallForm"
import PaywallOverlay from "@/components/PaywallV1/PaywallOverlay"

export default {
  name: "PaywallMenu",
  components: {
    PaywallOverlay,
    PaywallForm,
  },
  props: {
    config: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showOverlay: false,
      activeDevice: "ps4",
      newDeviceName: "",
      errorMsg: "* required",
      disableConfirm: true,
      showDeviceDeleteButton: false,
    }
  },
  computed: {
    ...mapGetters(['canPaywallEdit']),
    modalID: () => "add-new-device-modal",
    overlayMsg() {
      return `delete ${this.activeDevice}'s config?`
    },
    placeholder() {
      return {
        header: "Subscribe to MLB.TV",
        subheader: `Visit MLB.com/${this.newDeviceName} to purchase`,
        footer: `Blackout and other restrictions apply. Visit mlb.com/${this.newDeviceName} to learn more`,
        platformUrl: `mlb.com/${this.newDeviceName}`,
        offerGroups: [
          {
            name: "Yearly",
            packages: [
              {
                enabled: true,
                title: "MLB Account",
                subtitle: "Free for 7 days",
                text: "Watch the MLB.TV Free MLB Game of the Day.",
                type: "free",
                price: "Free",
                priceUnit: "/season",
                requiresTeamSelection: false,
                purchaseLink: `mlb.com/${this.newDeviceName}`,
                buttons: [
                  {
                    text: "button one",
                    enabled: true,
                  },
                  {
                    text: "button two",
                    enabled: false,
                  },
                ],
                tags: [
                  {
                    name: "Best Value",
                    bgColor: "#006EF5",
                    fgColor: "#FFFFFF",
                  },
                ],
                size: "medium",
              },
            ],
          },
          {
            name: "Monthly",
            packages: [
              {
                enabled: true,
                title: "MLB Account",
                subtitle: "Free for 7 days",
                text: "Watch the MLB.TV Free MLB Game of the Day.",
                type: "free",
                price: "Free",
                priceUnit: "/month",
                requiresTeamSelection: false,
                purchaseLink: `mlb.com/${this.newDeviceName}`,
                buttons: [
                  {
                    text: "button one",
                    enabled: true,
                  },
                  {
                    text: "button two",
                    enabled: false,
                  },
                ],
                tags: [
                  {
                    enabled: true,
                    name: "Best Value",
                    bgColor: "#006EF5",
                    fgColor: "#FFFFFF",
                  },
                ],
                size: "medium",
              },
            ],
          },
        ],
        rules: {
          header: {
            charMax: 200,
          },
          subheader: {
            charMax: 200,
          },
          footer: {
            charMax: 200,
          },
          platformUrl: {
            charMax: 200,
          },
          title: {
            charMax: 200,
          },
          subtitle: {
            charMax: 200,
          },
          price: {
            charMax: 200,
          },
          priceUnit: {
            charMax: 200,
          },
          text: {
            charMax: 200,
          },
          purchaseLink: {
            charMax: 200,
          },
        },
      }
    },
  },
  created() {
    // Ensure the rules for all fields are present for each platform, but prefer the existing values if already set.
    // This allows new rules to be added in the tool without them needing to be manually preloaded into every iteration
    // of the configs in all environments and supported languages. These field rules are not currently exposed for
    // editing by the tool, so the placeholder values above are the likely actual values unless manually overridden by
    // downloading the config, updating, and re-uploading.
    Object.keys(this.config).forEach(key => {
      this.config[key].rules = _.merge(this.placeholder.rules, this.config[key].rules)
    })
  },
  methods: {
    setActiveDevice(device) {
      this.activeDevice = device
    },
    onConfirm() {
      this.$delete(this.config, this.activeDevice)
      this.toggleOverlay()

      const devices = Object.keys(this.config)
      this.activeDevice = devices[devices.length - 1]
    },
    toggleOverlay() {
      this.showOverlay = !this.showOverlay
    },
    resetModal() {
      this.newDeviceName = ""
      this.errorMsg = "* required"
    },
    addDevice() {
      if (this.newDeviceName.length > 0) {
        this.$set(this.config, this.newDeviceName.toLowerCase(), this.placeholder)
        this.$bvModal.hide(this.modalID)
      }
    },
    isValid() {
      let allValid = true
      const {menuform} = this.$refs

      Object.keys(menuform).forEach(key => {
        if (!menuform[key].isValid()) allValid = false
      })
      return allValid
    },
    validate() {
      if (this.newDeviceName.length > 0) {
        const deviceExists = Object.keys(this.config).includes(this.newDeviceName.toLowerCase())

        if (deviceExists) {
          this.errorMsg = `* the device ${this.newDeviceName} already has a published config`
          this.disableConfirm = true
          return false
        } else {
          this.disableConfirm = false
          return true
        }
      }
      return null
    },
  },
}
</script>

<style scoped>
/* Set a fixed width for the vertical device tabs */
/deep/ .nav {
  width: 200px;
}
</style>
